import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Calendar, Timer, Plus, Search, WifiOff, AlertTriangle, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SessionCard from '../components/SessionCard';
import SessionCardSkeleton from '../components/SessionCardSkeleton';
import EmptyState from '../components/ui/EmptyState.jsx';
import PageShell from '../components/ui/PageShell';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import SegmentedControl from '../components/ui/SegmentedControl.jsx';
import { Input } from '../components/ui/Input.jsx';
import ActiveSessionBanner from '../components/ActiveSessionBanner';
import AuraLightBackground from '../components/ui/AuraLightBackground';
import SessionWeekView from '../components/session/SessionWeekView';
import { SESSION_STATUS, SESSION_TYPE, PARTICIPANT_STATUS } from '../constants';
import { findActiveParticipant, stripParticipantImages } from '../utils/participantUtils';
import { apiService } from '../services/api';
import browserCacheService from '../services/BrowserCacheService';
import { useCachedData } from '../hooks/useCachedData';
import { joinSessionAndNavigate } from '../services/sessionJoinActions';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_SESSIONS = [];

// Fehlerart -> Icon des Banners (Okt 2026). Vorher trug jede Fehlerart eine
// eigene Statusflaeche (blau, gelb, rot) und ein handgezeichnetes SVG. Jetzt
// ein ruhiger, neutraler Kasten; das Lucide-Icon sagt, was los ist.
const ERROR_ICONS = {
  offline: WifiOff,
  auth: Lock,
};

// Übersetzt API-Fehler in die Banner-Anzeige (offline/server/auth)
const classifyLoadError = (error) => {
  if (!error) return null;

  if (error.code === 'ERR_NETWORK' || !navigator.onLine) {
    return {
      type: 'offline',
      message: 'Keine Internetverbindung. Bitte prüfe deine Netzwerkverbindung.',
      canRetry: true
    };
  }
  const status = error.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return {
      type: 'server',
      message: 'Server vorübergehend nicht erreichbar. Der Versuch läuft automatisch weiter …',
      canRetry: true
    };
  }
  if (status === 401) {
    return {
      type: 'auth',
      message: 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.',
      canRetry: false
    };
  }
  if (status >= 500) {
    return {
      type: 'server',
      message: 'Server-Fehler. Bitte versuche es später erneut.',
      canRetry: true
    };
  }
  return { type: 'error', message: 'Fehler beim Laden der Sessions', canRetry: false };
};

const SessionsPage = ({ onJoinSession, onValidateCode, currentUser = null, embedded = false }) => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // 'list' oder 'week'
  const [searchTerm, setSearchTerm] = useState('');
  const [activeUserSession, setActiveUserSession] = useState(null);

  // Stale-While-Revalidate: gecachte Sessions werden sofort angezeigt,
  // der API-Call läuft im Hintergrund und aktualisiert still.
  // Key enthält schoolId UND userId, weil das Backend die Sichtbarkeit
  // pro User filtert (eigene private Sessions, Teilnahmen).
  const schoolId = authUser?.schoolId;
  const cacheKey = schoolId && authUser?.id ? `sessions_${schoolId}_${authUser.id}` : null;

  const fetchSessions = useCallback(async () => {
    // Lade alle relevanten Sessions für korrekte Counts. excludeFinished blendet
    // COMPLETED/CANCELLED bereits serverseitig aus (die Seite zeigt sie ohnehin
    // nicht) -> deutlich kleinere Antwort, je mehr Sessions die Schule ansammelt.
    const result = await apiService.getSessions({ schoolId, limit: 1000, excludeFinished: true });
    return stripParticipantImages(result?.data || result?.sessions || []);
  }, [schoolId]);

  const {
    data: loadedSessions,
    isLoading,
    error,
    retryCount,
    refresh
  } = useCachedData(cacheKey, fetchSessions, browserCacheService.TTL.SESSIONS);

  const sessions = loadedSessions || EMPTY_SESSIONS;

  const loadError = useMemo(() => {
    if (authUser && !schoolId) {
      return {
        type: 'warning',
        message: 'Keine Schul-ID verfügbar. Bitte melde dich erneut an.'
      };
    }
    return classifyLoadError(error);
  }, [authUser, schoolId, error]);

  // Find user's active session for banner (Zoom-Style: non-dismissable)
  useEffect(() => {
    if (!authUser?.id || !sessions.length) {
      setActiveUserSession(null);
      return;
    }

    const currentId = String(authUser.id);
    const userActiveSession = sessions.find(session => {
      // Show banner for ACTIVE sessions OR PLANNED sessions with validated user
      const isActive = session.status === SESSION_STATUS.ACTIVE;
      const isPlanned = session.status === SESSION_STATUS.PLANNED;

      // Check if user is host
      if (String(session.hostId) === currentId) {
        return isActive || isPlanned;
      }

      // Check if user is participant - WICHTIG: nur aktive Teilnahme zaehlt.
      // Das Backend liefert auch LEFT-Records mit (Audit-Trail); ohne Filter
      // "laeuft" eine verlassene Session hier im Banner weiter.
      const userParticipant = findActiveParticipant(session.participants, currentId);

      if (userParticipant) {
        // For ACTIVE sessions, show for any active participant
        if (isActive) return true;

        // For PLANNED sessions, only show if user is validated
        if (isPlanned && userParticipant.status === PARTICIPANT_STATUS.VALIDATED) {
          return true;
        }
      }

      return false;
    });

    setActiveUserSession(userActiveSession);
  }, [sessions, authUser?.id]);

  // Memoized session filtering and counting
  const { activeSessions, plannedSessions, filteredSessions } = useMemo(() => {
    const safeSessions = sessions || [];

    // ✅ FIX: Filtere COMPLETED Sessions heraus - nur aktive oder geplante Sessions
    const nonCompletedSessions = safeSessions.filter(s => {
      const status = s.status?.toUpperCase();
      return status !== 'COMPLETED' && status !== 'CANCELLED';
    });

    const active = nonCompletedSessions.filter(s =>
      s.status === SESSION_STATUS.ACTIVE ||
      s.status === 'WAITING' ||
      s.status?.toUpperCase() === 'WAITING'
    );
    const planned = nonCompletedSessions.filter(s =>
      s.status === SESSION_STATUS.PLANNED ||
      s.status?.toUpperCase() === 'PLANNED'
    );

    // Apply tab filter - nur auf nicht-abgeschlossene Sessions
    let filtered = nonCompletedSessions;
    if (activeTab === 'open') {
      filtered = nonCompletedSessions.filter(s => s.type === SESSION_TYPE.OPEN || s.type === 'OPEN');
    } else if (activeTab === 'private') {
      filtered = nonCompletedSessions.filter(s => s.type === SESSION_TYPE.PRIVATE || s.type === 'PRIVATE');
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(session =>
        session.topic?.toLowerCase().includes(searchLower) ||
        session.description?.toLowerCase().includes(searchLower) ||
        session.subject?.toLowerCase().includes(searchLower)
      );
    }

    return {
      activeSessions: active,
      plannedSessions: planned,
      filteredSessions: filtered
    };
  }, [sessions, activeTab, searchTerm]);

  // ✅ FIX: Session counts for tabs - NUR aktive/geplante Sessions zählen (keine COMPLETED/CANCELLED)
  // CRITICAL: Ignoriere Backend-Counts und berechne immer aus gefilterten Sessions!
  // Das Backend sendet möglicherweise Counts inkl. COMPLETED/CANCELLED
  // ⚡ Perf: memoisiert auf [sessions]. Vorher liefen diese 4 Filter-Durchläufe über
  // bis zu 1000 Sessions bei JEDEM Render neu (also bei jedem Such-Tastendruck und
  // jedem Tab-Wechsel), obwohl sie nur von der Session-Liste abhängen.
  const { allCount, openCount, privateCount } = useMemo(() => {
    const nonCompleted = (sessions || []).filter(s => {
      const status = s.status?.toUpperCase() || s.status;
      return status !== 'COMPLETED' && status !== 'CANCELLED';
    });
    // Immer aus Frontend-Daten berechnen, nicht Backend-Counts verwenden!
    return {
      allCount: nonCompleted.length,
      openCount: nonCompleted.filter(s => s.type === SESSION_TYPE.OPEN || s.type === 'OPEN').length,
      privateCount: nonCompleted.filter(s => s.type === SESSION_TYPE.PRIVATE || s.type === 'PRIVATE').length,
    };
  }, [sessions]);


  // Determine if banner should be shown based on timing (moved before conditional returns)
  const shouldShowBanner = useMemo(() => {
    if (!activeUserSession) return false;

    const isActive = activeUserSession.status === SESSION_STATUS.ACTIVE;
    const isPlanned = activeUserSession.status === SESSION_STATUS.PLANNED;

    // Show immediately for active sessions
    if (isActive) return true;

    // For planned sessions, only show 15 minutes before start
    if (isPlanned && activeUserSession.startTime) {
      const startTime = new Date(activeUserSession.startTime);
      const now = new Date();
      const minutesUntilStart = (startTime - now) / (1000 * 60);
      return minutesUntilStart <= 15 && minutesUntilStart >= 0;
    }

    return false;
  }, [activeUserSession]);

  // Filter sessions by current tab and search
  // ⚡ Perf: memoisiert. Vorher lief getSessionsForSection bei jedem Render zweimal
  // (active + planned) über die Listen - bei getippter Suche einmal pro Anschlag.
  // Jetzt nur neu, wenn sich Liste, Tab oder Suchbegriff wirklich ändern.
  const { filteredActiveSessions, filteredPlannedSessions } = useMemo(() => {
    const filterForSection = (sectionSessions) => sectionSessions.filter(session => {
      // Tab filter
      const tabMatch = activeTab === 'all' ||
        (activeTab === 'open' && session.type === SESSION_TYPE.OPEN) ||
        (activeTab === 'private' && session.type === SESSION_TYPE.PRIVATE);

      // Search filter
      let searchMatch = true;
      if (searchTerm.trim()) {
        const searchLower = searchTerm.toLowerCase();
        searchMatch = session.topic?.toLowerCase().includes(searchLower) ||
          session.description?.toLowerCase().includes(searchLower) ||
          session.subject?.toLowerCase().includes(searchLower);
      }

      return tabMatch && searchMatch;
    });

    return {
      filteredActiveSessions: filterForSection(activeSessions),
      filteredPlannedSessions: filterForSection(plannedSessions),
    };
  }, [activeSessions, plannedSessions, activeTab, searchTerm]);

  // Stabile Referenz, damit React.memo(SessionCard) bei Such-/Tab-Updates greift
  const handleJoinClick = useCallback(async (sessionId) => {
    try {
      // Gemeinsamer Join-Flow: Toast (nach participantStatus), justJoined-Overlay, Navigation
      await joinSessionAndNavigate(sessionId, navigate, { joinContext: 'OPEN_LIST' });
    } catch (error) {
      // Fehler-Toast kommt bereits aus joinSessionAndNavigate - re-throwen,
      // damit SessionCard seine "Platz reserviert!"-Bestaetigung nicht zeigt.
      console.error('Error joining session:', error);
      throw error;
    }
  }, [navigate]);

  const handleBackClick = () => {
    navigate('/app');
  };

  const handleCreateSession = () => {
    // Navigate to the create session page
    navigate('/create-session');
  };

  // Check if we have any sessions after filtering
  const hasFilteredSessions = filteredActiveSessions.length > 0 || filteredPlannedSessions.length > 0;
  const isSearching = searchTerm.trim().length > 0;

  // Retry handler for manual retry button: erneut laden statt Full-Page-Reload
  const handleRetry = refresh;

  // Erstbesuch ohne Cache: Skeleton-Cards im Grid statt Full-Page-Spinner
  // (Header, Suche und Tabs bleiben sichtbar und sofort bedienbar)
  const showSkeletons = isLoading && !loadedSessions;

  return (
    <div className={embedded ? 'relative' : 'min-h-screen relative'}>
      {!embedded && <AuraLightBackground intensity="normal" showAnimations={true} />}

      {/* Embedded: MainScreen liefert Rahmen + Bottom-Nav, PageShell nur den Kopf. */}
      {embedded && (
        <PageShell
          variant="main"
          embedded
          title="Lernsessions"
          actions={!(activeUserSession?.status === SESSION_STATUS.ACTIVE) && (
            <Button size="sm" onClick={handleCreateSession} aria-label="Neue Session erstellen">
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Neue Session</span>
              <span className="sm:hidden">Session</span>
            </Button>
          )}
        />
      )}

      {/* Standalone Compact Header - Always at top */}
      {!embedded && (
      <div className="sticky top-0 z-40 border-b border-border relative bg-surface">
        {/* Active Session Banner - klebt mit dem Header oben und liegt immer darüber (konsistent zu MainScreen) */}
        {shouldShowBanner && (
          <ActiveSessionBanner
            activeSession={activeUserSession}
            onDismiss={null}
          />
        )}
        {/* Derselbe Kopf wie im eingebetteten Fall — nur mit Rueckweg, weil dies
            eine eigene Route ist. Der sticky Rahmen bleibt hier, weil der
            ActiveSessionBanner mit ihm oben kleben muss. */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <PageShell
            variant="main"
            embedded
            title="Lernsessions"
            onBack={handleBackClick}
            actions={!(activeUserSession?.status === SESSION_STATUS.ACTIVE) && (
              <Button size="sm" onClick={handleCreateSession} aria-label="Neue Session erstellen">
                <Plus className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Neue Session</span>
              </Button>
            )}
          />
        </div>
      </div>
      )}

      {/* Main Content
          Als eigene Route fehlte hier bis August 2026 jeder Abstand am Seitenende —
          die letzte Session-Karte klebte am unteren Bildschirmrand bzw. am
          Home-Indikator. Eingebettet liefert MainScreen das Polster.
          max-w-6xl ist dieselbe Breite, die PageShell als Rolle `list` vergibt. */}
      <div
        className={embedded ? 'relative z-10' : 'p-4 sm:p-6 relative z-10'}
        style={embedded ? undefined : { paddingBottom: 'var(--page-bottom-plain)' }}
      >
        <div className="max-w-6xl mx-auto space-y-4">

          {/* Error Banner: neutral, ein Satz, ohne Versuchszaehler */}
          {loadError && (() => {
            const ErrorIcon = ERROR_ICONS[loadError.type] || AlertTriangle;
            return (
              <div className="rounded-xl border border-border bg-surface-raised p-4">
                <div className="flex items-start gap-3">
                  <ErrorIcon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="flex-1">
                    <h3 className="mb-1 font-semibold text-foreground">
                      {loadError.type === 'offline' && 'Keine Verbindung'}
                      {loadError.type === 'auth' && 'Anmeldung erforderlich'}
                      {loadError.type === 'server' && 'Server-Problem'}
                      {loadError.type === 'warning' && 'Hinweis'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {loadError.message}
                    </p>
                    {loadError.canRetry && retryCount >= 2 && (
                      <Button variant="secondary" size="sm" onClick={handleRetry} className="mt-3">
                        Erneut versuchen
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Suche in voller Breite; darunter Filter und Ansicht (Okt 2026).
              flex-wrap: Auf schmalen Telefonen rutscht die Ansicht in eine
              eigene Zeile, statt seitlich ueberzustehen. */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="w-full sm:flex-1">
              <Input
                type="search"
                leadingIcon={Search}
                placeholder="Sessions durchsuchen"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Sessions suchen"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              {/* Filter — kanonische Tab-Leiste statt eigenem Segment-Control */}
              <Tabs
                variant="pill"
                ariaLabel="Sessions filtern"
                value={activeTab}
                onChange={setActiveTab}
                items={[
                  { key: 'all', label: 'Alle', count: allCount },
                  { key: 'open', label: 'Öffentlich', shortLabel: 'Offen', count: openCount },
                  { key: 'private', label: 'Privat', count: privateCount },
                ]}
              />
              {/* Ansicht: Liste / Woche — der Baustein loest die Haptik selbst aus */}
              <SegmentedControl
                as="radio"
                size="sm"
                ariaLabel="Ansicht"
                value={viewMode}
                onChange={setViewMode}
                className="ml-auto w-36 shrink-0"
                options={[
                  { value: 'list', label: 'Liste' },
                  { value: 'week', label: 'Woche' },
                ]}
              />
            </div>
          </div>

          {/* Retry-Hinweis während automatischer Wiederholungsversuche */}
          {isLoading && retryCount > 0 && (
            <p className="text-sm text-muted-foreground px-2">
              Verbindung wird wiederhergestellt …
            </p>
          )}

          {/* Wochenansicht */}
          {viewMode === 'week' && (
            <SessionWeekView
              sessions={filteredSessions}
              onJoinSession={handleJoinClick}
            />
          )}

          {/* Listenansicht */}
          {viewMode === 'list' && (
            <>
              {/* No Results Message */}
              {isSearching && !hasFilteredSessions && (
                <EmptyState
                  icon={Search}
                  size="sm"
                  text={`Nichts passt zu „${searchTerm.trim()}“.`}
                />
              )}

              {/* Active Sessions */}
              <div className="space-y-3">
                <div className="flex items-baseline gap-2 px-1">
                  <h2 className="font-semibold text-foreground">Aktive Sessions</h2>
                  <span className="text-sm tabular-nums text-muted-foreground">{filteredActiveSessions.length}</span>
                </div>

                {/* `stagger-children`: die Karten trudeln nacheinander ein statt alle
                    auf einen Schlag. Reines CSS (index.css, Kapitel "Handschrift"),
                    kein Prop pro Karte, kein Index — und die Verzoegerungen sind
                    absichtlich krumm, damit die Liste keinen Metronom-Takt hat. */}
                {/* Zwei Spalten erst ab lg: Ab md (768px) blieben neben der
                    Navigationsspalte rund 312px je Karte — schmaler als auf
                    jedem Telefon. Gleich hohe Zeilen (kein items-start):
                    unterschiedlich hohe Karten nebeneinander lassen die Liste
                    ausgefranst wirken. Die Stagger-Animation laeuft in
                    Dokumentreihenfolge weiter und liest sich im Raster als
                    Zeilenaufbau. */}
                <div className="grid gap-3 lg:grid-cols-2 stagger-children" id="sessions-aktive">
                  {showSkeletons ? (
                    <SessionCardSkeleton />
                  ) : filteredActiveSessions.length > 0 ? (
                    filteredActiveSessions.map(session => (
                      <SessionCard
                        key={session.id}
                        session={session}
                        currentUser={currentUser}
                        onJoinSession={handleJoinClick}
                        onValidateCode={onValidateCode}
                      />
                    ))
                  ) : (
                    <EmptyState icon={Timer} size="sm" title="Keine laufenden Sessions" />
                  )}
                </div>
              </div>

              {/* Planned Sessions */}
              <div className="space-y-3">
                <div className="flex items-baseline gap-2 px-1">
                  <h2 className="font-semibold text-foreground">Geplante Sessions</h2>
                  <span className="text-sm tabular-nums text-muted-foreground">{filteredPlannedSessions.length}</span>
                </div>

                {/* Zwei Spalten erst ab lg: Ab md (768px) blieben neben der
                    Navigationsspalte rund 312px je Karte — schmaler als auf
                    jedem Telefon. Gleich hohe Zeilen (kein items-start):
                    unterschiedlich hohe Karten nebeneinander lassen die Liste
                    ausgefranst wirken. Die Stagger-Animation laeuft in
                    Dokumentreihenfolge weiter und liest sich im Raster als
                    Zeilenaufbau. */}
                <div className="grid gap-3 lg:grid-cols-2 stagger-children" id="sessions-geplante">
                  {showSkeletons ? (
                    <>
                      <SessionCardSkeleton />
                      <SessionCardSkeleton />
                    </>
                  ) : filteredPlannedSessions.length > 0 ? (
                    filteredPlannedSessions.map(session => (
                      <SessionCard
                        key={session.id}
                        session={session}
                        currentUser={currentUser}
                        onJoinSession={handleJoinClick}
                        onValidateCode={onValidateCode}
                      />
                    ))
                  ) : (
                    <EmptyState icon={Calendar} size="sm" title="Keine geplanten Sessions" />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

SessionsPage.propTypes = {
  onJoinSession: PropTypes.func.isRequired,
  onValidateCode: PropTypes.func,
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string, // Optional, da currentUser selbst optional ist
    image: PropTypes.string,
    level: PropTypes.number
  }),
  embedded: PropTypes.bool
};

// defaultProps removed - using JavaScript default parameters instead

export default SessionsPage;
